import type { ReactNode } from 'react';
import { ArrowDownRight, ArrowUpRight } from 'lucide-react';
import { Card } from './Card';
import { IconTile } from './IconTile';
import { cn } from '@/lib/utils';

interface StatTileProps {
  /** Versal spärrad etikett över siffran. */
  label: string;
  /** Själva nyckeltalet. Sträng, så "4,8/5" och "487 200" kan skrivas som de ska. */
  value: ReactNode;
  icon?: ReactNode;
  tone?: 'brand' | 'success' | 'warning' | 'danger' | 'neutral' | 'info';
  /**
   * Förändring sedan förra perioden. `riktning` styr färg och pil separat från
   * tecknet: en NEDGÅNG i öppna ärenden är bra, en nedgång i nöjdhet är dålig,
   * och komponenten kan inte veta vilket. Den som anropar får säga det.
   */
  delta?: { text: string; riktning: 'bra' | 'daligt' | 'neutral' };
  /** Kort förklaring efter delta, t.ex. "denna månad". */
  hint?: string;
  className?: string;
}

/**
 * Nyckeltalskort enligt referensbilden: tyst versal etikett, stor tajt siffra,
 * tonad ikonbricka uppe till höger, och en rad med förändring underst.
 *
 * Ingen sparkline. En kurva på fyra datapunkter ser ut som information men bär
 * ingen — och ett nyckeltal som antyder en trend det inte kan belägga är
 * sämre än ett som bara visar siffran.
 */
const deltaFarger = {
  bra: 'bg-emerald-50 text-emerald-700',
  daligt: 'bg-red-50 text-red-700',
  neutral: 'bg-ink-100 text-ink-600',
};

export function StatTile({
  label,
  value,
  icon,
  tone = 'brand',
  delta,
  hint,
  className,
}: StatTileProps) {
  const Pil = delta?.riktning === 'daligt' ? ArrowDownRight : ArrowUpRight;

  return (
    <Card className={cn('p-4', className)}>
      <div className="flex items-start justify-between gap-3">
        <span className="text-metric-label pt-0.5">{label}</span>
        {icon && <IconTile icon={icon} tone={tone} size="sm" />}
      </div>

      <div className="text-metric mt-3">{value}</div>

      {(delta || hint) && (
        <div className="mt-2.5 flex items-center gap-2 flex-wrap">
          {delta && (
            <span
              className={cn(
                'inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10.5px] font-bold tabular-nums',
                deltaFarger[delta.riktning],
              )}
            >
              {delta.riktning !== 'neutral' && <Pil size={11} strokeWidth={2.5} aria-hidden />}
              {delta.text}
            </span>
          )}
          {hint && <span className="text-[11px] font-medium text-ink-500">{hint}</span>}
        </div>
      )}
    </Card>
  );
}
